import type { DudaAnalyticsCacheV1 } from './dudaAnalyticsTypes';

export type AnalyticsDataset = 'traffic_total' | 'traffic_geo' | 'traffic_system' | 'activities';

export type AnalyticsFlatRow = {
  id: string;
  siteName: string;
  dataset: AnalyticsDataset;
  period?: string;
  dimensionLabel?: string;
  visitors?: number;
  visits?: number;
  pageViews?: number;
  clickToCalls?: number;
  clickToMaps?: number;
  clickToEmails?: number;
  formSubmits?: number;
};

function pickMetric(obj: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'number' && !Number.isNaN(v)) {
      return v;
    }
    const alt = k.toLowerCase();
    const v2 = obj[alt];
    if (typeof v2 === 'number' && !Number.isNaN(v2)) {
      return v2;
    }
  }
  return undefined;
}

function formatDimension(dim: unknown): string {
  if (!dim || typeof dim !== 'object') {
    return '';
  }
  return Object.entries(dim as Record<string, unknown>)
    .map(([k, v]) => `${k}: ${String(v)}`)
    .join(' · ');
}

function pushTrafficRow(
  rows: AnalyticsFlatRow[],
  siteName: string,
  dataset: AnalyticsDataset,
  period: string,
  dimensionLabel: string | undefined,
  dataObj: Record<string, unknown>
): void {
  const visitors = pickMetric(dataObj, 'VISITORS', 'VISITOR', 'unique_visitors', 'UniqueVisitors');
  const visits = pickMetric(dataObj, 'VISITS', 'visits');
  const pageViews = pickMetric(dataObj, 'PAGE_VIEWS', 'PAGEVIEWS', 'pageviews', 'PageViews');
  const id = `${siteName}|${dataset}|${period}|${dimensionLabel ?? ''}|${rows.length}`;
  rows.push({
    id,
    siteName,
    dataset,
    period: period || undefined,
    dimensionLabel: dimensionLabel || undefined,
    visitors,
    visits,
    pageViews,
  });
}

function flattenTrafficBlob(
  siteName: string,
  dataset: 'traffic_total',
  blob: unknown,
  rows: AnalyticsFlatRow[]
): void {
  if (blob == null) {
    return;
  }
  if (Array.isArray(blob)) {
    blob.forEach((item, i) => {
      if (!item || typeof item !== 'object') {
        return;
      }
      const o = item as Record<string, unknown>;
      const period = String(o.date ?? o.period ?? o.week ?? o.month ?? o.year ?? '');
      const inner =
        o.data && typeof o.data === 'object' ? (o.data as Record<string, unknown>) : o;
      const dim = o.dimension ? formatDimension(o.dimension) : undefined;
      pushTrafficRow(rows, siteName, dataset, period || `#${i}`, dim, inner);
    });
    return;
  }
  if (typeof blob === 'object') {
    pushTrafficRow(rows, siteName, dataset, '', undefined, blob as Record<string, unknown>);
  }
}

function flattenDimensionedTraffic(
  siteName: string,
  dataset: 'traffic_geo' | 'traffic_system',
  blob: unknown,
  rows: AnalyticsFlatRow[]
): void {
  if (blob == null || !Array.isArray(blob)) {
    return;
  }
  blob.forEach((item, i) => {
    if (!item || typeof item !== 'object') {
      return;
    }
    const o = item as Record<string, unknown>;
    const dimLabel = formatDimension(o.dimension);
    const data = o.data;
    if (data && typeof data === 'object') {
      pushTrafficRow(rows, siteName, dataset, '', dimLabel || `row ${i}`, data as Record<string, unknown>);
    }
  });
}

function pushActivityRow(
  rows: AnalyticsFlatRow[],
  siteName: string,
  period: string,
  o: Record<string, unknown>
): void {
  const id = `${siteName}|activities|${period}|${rows.length}`;
  rows.push({
    id,
    siteName,
    dataset: 'activities',
    period: period || undefined,
    clickToCalls: pickMetric(o, 'CLICK_TO_CALLS', 'click_to_calls'),
    clickToMaps: pickMetric(o, 'CLICK_TO_MAPS', 'click_to_maps'),
    clickToEmails: pickMetric(o, 'CLICK_TO_EMAILS', 'click_to_emails'),
    formSubmits: pickMetric(o, 'FORM_SUBMITS', 'form_submits'),
  });
}

function flattenActivities(siteName: string, blob: unknown, rows: AnalyticsFlatRow[]): void {
  if (blob == null) {
    return;
  }
  if (Array.isArray(blob)) {
    blob.forEach((item, i) => {
      if (!item || typeof item !== 'object') {
        return;
      }
      const o = item as Record<string, unknown>;
      const period = String(o.date ?? o.period ?? o.month ?? '');
      pushActivityRow(rows, siteName, period || `#${i}`, o);
    });
    return;
  }
  if (typeof blob === 'object') {
    pushActivityRow(rows, siteName, '', blob as Record<string, unknown>);
  }
}

/** Normalises cached snapshots into table rows for filtering / export. */
export function flattenDudaAnalyticsCache(cache: DudaAnalyticsCacheV1): AnalyticsFlatRow[] {
  const rows: AnalyticsFlatRow[] = [];
  for (const snap of Object.values(cache.sites)) {
    flattenTrafficBlob(snap.siteName, 'traffic_total', snap.trafficSummary, rows);
    flattenDimensionedTraffic(snap.siteName, 'traffic_geo', snap.trafficByGeo, rows);
    flattenDimensionedTraffic(snap.siteName, 'traffic_system', snap.trafficBySystem, rows);
    flattenActivities(snap.siteName, snap.activities, rows);
  }
  return rows;
}

export function datasetLabel(d: AnalyticsDataset): string {
  switch (d) {
    case 'traffic_total':
      return 'Traffic (totals / time series)';
    case 'traffic_geo':
      return 'Traffic by geography';
    case 'traffic_system':
      return 'Traffic by system (browser / OS)';
    case 'activities':
      return 'Activities (forms, clicks)';
    default:
      return d;
  }
}
